<script lang="ts">
	import Shape from '#components/shape.svelte'
	import Render from '#components/render.svelte'
	import { linky } from '#library/linky.ts'
	import type { StoryblokAsset, StoryblokRichtext } from '#library/components.schema.build.ts'

	interface Props {
		suptitle?: string
		title: string
		subtitle?: string
		body?: StoryblokRichtext
		image: StoryblokAsset
		theme?: string
		inline?: boolean
		count?: number
		i?: number
		href?: string
		split?: boolean
		shape?: string
	}

	let {
		suptitle,
		title,
		subtitle,
		body,
		image,
		theme,
		inline = undefined,
		count,
		i = 0,
		split = false,
		shape = '5_6-02',
		...rest
	}: Props = $props()

	const href = $derived(linky`${rest.href}`)
</script>

<svelte:element
	this={href ? 'a' : 'div'}
	class="card {theme || 'theme-pink-on-lupton'}"
	class:inline
	class:href
	href={href || ''}
>
	<Shape src={image.filename || ''} alt={title} name={shape as never} focus={image.focus} />

	<div class:link={href}>
		<div>
			{#if suptitle}
				<div class="suptitle">{suptitle.replace('{{pagination}}', `${i + 1} of ${count}`)}</div>
			{/if}
			{#if title}
				<div class="title" class:split>{title}</div>
			{/if}
			{#if subtitle}
				<div class="subtitle">{subtitle}</div>
			{/if}
		</div>
		{#if href}
			<div class="icon">
				<svg class="svg">
					<use xlink:href="/images/vector.svg#arrow-up-right"></use>
				</svg>
				<svg class="svg">
					<use xlink:href="/images/vector.svg#arrow-up-right"></use>
				</svg>
			</div>
		{/if}
		{#if body}
			<div class="body">
				<Render content={body} />
			</div>
		{/if}
	</div>
</svelte:element>

<style lang="css">
	.card {
		background-color: var(--c-background);
		color: var(--c-foreground);
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		grid-column: 1 / -1;
		padding: 1rem 1.25rem 1.25rem;
		text-align: start;
		transition: scale 0.2s ease;
		scale: var(--scale);

		@media (--from-tablet) {
			grid-column: span 4;
			padding: 1.25rem 1.5rem 1.5rem;
		}

		&.href:hover {
			scale: 1.025;

			.svg {
				translate: 100% -100%;

				& + .svg {
					translate: 0% 0%;
				}
			}
		}

		&.inline {
			align-items: center;
			display: grid;
			gap: 1rem;
			margin-inline: 5vw;
			padding: 0 1rem;
			width: 90vw;

			@media (--from-mobile) {
				gap: 4rem;
				grid-template-columns: 26.25rem 1fr;
				margin-inline: 0;
				padding: 4rem;
				width: auto;
			}

			:global {
				.shape {
					margin-block-start: auto;
				}
			}
		}

		:global {
			.shape {
				margin-block-end: auto;
				padding-inline: 0.625rem;
			}
		}
	}

	.link {
		display: flex;
		gap: 1rem;
	}

	.icon {
		align-self: end;
		margin: 0 0 0.25rem auto;
		overflow: clip;
		size: 2rem;
	}

	.svg {
		transition: translate 0.3s ease;
		position: absolute;
		size: 100%;

		& + .svg {
			translate: -100% 100%;
		}
	}

	.suptitle {
		font: 400 var(--f-h6);
		letter-spacing: var(--ls-h6);
	}

	.title {
		font: 500 var(--f-h6);
		letter-spacing: var(--ls-h6);
		margin-top: 0.5rem;

		&.split {
			font: 500 var(--f-h3);
			margin-top: 1rem;
			letter-spacing: var(--ls-h3);
		}
	}

	.subtitle {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-start: 0.25rem;

		@media (--from-mobile) {
			margin-block-start: 0;
		}
	}

	.body {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}
</style>
